import { Monitor, Moon, Sun } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useTheme } from "@/lib/use-theme";
import { cn } from "@/lib/utils";
import type { Theme } from "@/lib/theme";

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

/**
 * Three explicit choices rather than a two-way switch.
 *
 * A switch cannot express "follow the OS", which is the right default and the
 * one that keeps working when someone's machine flips at sunset. Making it a
 * cycle button instead would hide the third state behind a click nobody
 * knows to make.
 */
export function ThemeToggle() {
  const { theme, resolved, setTheme } = useTheme();
  const Icon = resolved === "dark" ? Moon : Sun;

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        {/*
          `asChild` rather than a tooltip on the button: the trigger has to be
          the element Radix clones, and a tooltip wraps it in one Radix cannot
          see through. `title` is what is left to name it once the sidebar has
          collapsed to icons.
        */}
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton aria-label={`Theme: ${theme}`} title="Theme">
            <Icon aria-hidden />
            <span>Theme</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="right" align="end">
          {OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => {
                setTheme(option.value);
              }}
              className={cn(theme === option.value && "bg-accent")}
            >
              <option.icon className="size-4" aria-hidden />
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
